import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { Select } from "../../../components/Select";
import { useToast } from "../../../components/Toast";
import { useAuth } from "../../../auth/AuthContext";
import { formatBytes, formatDate } from "../../../lib/format";
import type { ApiError } from "../../../api/client";
import {
  LOT_DOCUMENT_KINDS,
  lotDocumentApi,
  type LotDocument,
  type LotDocumentKind,
  type LotDocumentState,
} from "../../../api/lotDocuments";
import LotDocumentForm from "./LotDocumentForm";

type Filter = "PENDING" | "RELEASED" | "WITHDRAWN" | "ALL";

/**
 * Sertifikat analize i izjava o NON GMO statusu, po LOT-u.
 *
 * Ekran drži tri koraka koji su posao tri čoveka, i razdvojenost je ono što se ovde čuva. Laboratorija
 * prilaže dokument; direktor kvaliteta ga pušta kupcu; kupac ga preuzima sa svog računa. Dokument koji nije
 * pušten kupac ne vidi — baza to odbija i bez ovog ekrana, a ekran to kaže rečima.
 *
 * Povučen dokument je svoje stanje, ne isto što i dokument koji čeka. Jedan nije nikad pušten, drugi je bio
 * i više nije, a odobrenje mu ostaje na redu jer ko ga je pustio i kada ne prestaje da bude istina.
 */
export default function LotDocumentsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";

  const smeDaPrilaze = hasPermission("lot_documents:manage");
  const canDelete = hasPermission("section:lot_documents:full");
  const smeDaPusta = hasPermission("lot_documents:approve");

  const [rows, setRows] = useState<LotDocument[] | null>(null);
  const [filter, setFilter] = useState<Filter>("PENDING");
  const [kind, setKind] = useState<"" | LotDocumentKind>("");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(
        await lotDocumentApi.search({
          state: filter,
          kind: kind || undefined,
          q: q.trim() || undefined,
        }),
      );
    } catch (e) {
      toast.error((e as ApiError).message);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, kind, q]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, kind]);

  function kindLabel(k: LotDocumentKind): string {
    if (k === "COA") return t("lotDoc.kind.COA", "Sertifikat analize");
    if (k === "NON_GMO") return t("lotDoc.kind.NON_GMO", "Izjava o NON GMO statusu");
    if (k === "HEALTH_CERTIFICATE") return t("lotDoc.kind.HEALTH_CERTIFICATE", "Zdravstveni sertifikat");
    return t("lotDoc.kind.OTHER", "Drugo");
  }

  function stateTag(s: LotDocumentState) {
    if (s === "RELEASED") return <span className="tag ok">{t("lotDoc.state.RELEASED", "pušten kupcu")}</span>;
    if (s === "WITHDRAWN") return <span className="tag neutral">{t("lotDoc.state.WITHDRAWN", "povučen")}</span>;
    return <span className="tag warn">{t("lotDoc.state.PENDING", "čeka puštanje")}</span>;
  }

  async function run(id: string, action: () => Promise<unknown>) {
    setBusy(id);
    try {
      await action();
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    } finally {
      setBusy(null);
    }
  }

  async function download(d: LotDocument) {
    try {
      const { url } = await lotDocumentApi.downloadUrl(d.id);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  /** Jedini presek koji ovaj ekran duguje: koliko čeka odluku, i koliko je pušteno. */
  const presek = useMemo(() => {
    const all = rows ?? [];
    return {
      ukupno: all.length,
      naCekanju: all.filter((d) => d.state === "PENDING").length,
      pusteno: all.filter((d) => d.state === "RELEASED").length,
      istekli: all.filter((d) => d.expired).length,
    };
  }, [rows]);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("lotDoc.title", "Dokumenti uz LOT")}</h1>
          <div className="sub">
            {t(
              "lotDoc.lead",
              "Sertifikat analize i izjava o NON GMO statusu idu po LOT-u. Laboratorija ih prilaže, direktor kvaliteta ih pušta kupcu, kupac ih preuzima sa svog računa.",
            )}
          </div>
        </div>
        {smeDaPrilaze && !adding && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => setAdding(true)}>
              {t("lotDoc.new", "Nov dokument")}
            </button>
          </div>
        )}
      </div>

      {smeDaPrilaze && adding && (
        <LotDocumentForm
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            void load();
          }}
        />
      )}

      <div className="card">
        <div className="card-b">
          <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
            <div className="seg">
              <button type="button" aria-pressed={filter === "PENDING"} onClick={() => setFilter("PENDING")}>
                {t("lotDoc.filter.pending", "Čeka puštanje")}
              </button>
              <button type="button" aria-pressed={filter === "RELEASED"} onClick={() => setFilter("RELEASED")}>
                {t("lotDoc.filter.released", "Pušteno kupcu")}
              </button>
              <button
                type="button"
                aria-pressed={filter === "WITHDRAWN"}
                onClick={() => setFilter("WITHDRAWN")}
              >
                {t("lotDoc.filter.withdrawn", "Povučeno")}
              </button>
              <button type="button" aria-pressed={filter === "ALL"} onClick={() => setFilter("ALL")}>
                {t("lotDoc.filter.all", "Sve")}
              </button>
            </div>
            <Select
              value={kind}
              onChange={(v) => setKind(v as "" | LotDocumentKind)}
              placeholder={t("lotDoc.allKinds", "sve vrste")}
              options={[
                { value: "", label: t("lotDoc.allKinds", "sve vrste") },
                ...LOT_DOCUMENT_KINDS.map((k) => ({ value: k, label: kindLabel(k) })),
              ]}
            />
            <input
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("lotDoc.search", "broj dokumenta ili ime fajla")}
            />
            <button type="button" className="btn sm" onClick={() => void load()}>
              {t("common.search", "Traži")}
            </button>
          </div>
        </div>
      </div>

      {rows === null ? (
        <PanelLoading text={t("lotDoc.loading", "Čitam dokumente…")} />
      ) : rows.length === 0 ? (
        <div className="empty">{t("lotDoc.none", "Nema dokumenata za ove uslove.")}</div>
      ) : (
        <>
          <div className="kpi-grid">
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("lotDoc.kpi.total", "dokumenata")}</div>
                <strong style={{ fontSize: 22 }}>{presek.ukupno}</strong>
              </div>
            </div>
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("lotDoc.kpi.pending", "čeka puštanje")}</div>
                <strong style={{ fontSize: 22 }}>{presek.naCekanju}</strong>
              </div>
            </div>
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("lotDoc.kpi.released", "pušteno kupcu")}</div>
                <strong style={{ fontSize: 22 }}>{presek.pusteno}</strong>
              </div>
            </div>
            <div className="card">
              <div className="card-b">
                <div className="muted">{t("lotDoc.kpi.expired", "prošao rok važenja")}</div>
                <strong style={{ fontSize: 22 }}>{presek.istekli}</strong>
              </div>
            </div>
          </div>

          <table className="tbl">
            <thead>
              <tr>
                <th>{t("lotDoc.col.lot", "LOT")}</th>
                <th>{t("lotDoc.col.product", "Proizvod")}</th>
                <th>{t("lotDoc.col.kind", "Vrsta")}</th>
                <th>{t("lotDoc.col.issueNumber", "Broj")}</th>
                <th>{t("lotDoc.col.validUntil", "Važi do")}</th>
                <th>{t("lotDoc.col.state", "Stanje")}</th>
                <th>{t("lotDoc.col.file", "Fajl")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>{d.lotDesignation ?? "—"}</td>
                  <td>{d.lotProductName ?? "—"}</td>
                  <td>{kindLabel(d.kind)}</td>
                  <td>{d.issueNumber ?? "—"}</td>
                  <td>
                    {d.validUntil ? formatDate(d.validUntil, lang) : "—"}
                    {d.expired && (
                      <>
                        {" "}
                        <span className="tag err">{t("lotDoc.expired", "istekao")}</span>
                      </>
                    )}
                  </td>
                  <td>{stateTag(d.state)}</td>
                  <td>
                    <button type="button" className="btn ghost sm" onClick={() => void download(d)}>
                      {d.fileName}
                    </button>
                    <div className="muted">{d.sizeBytes == null ? "" : formatBytes(d.sizeBytes)}</div>
                  </td>
                  <td>
                    <div className="toolbar" style={{ gap: 6, justifyContent: "flex-end" }}>
                      {smeDaPusta && d.state !== "RELEASED" && (
                        <button
                          type="button"
                          className="btn sm"
                          disabled={busy === d.id}
                          onClick={() => void run(d.id, () => lotDocumentApi.release(d.id))}
                        >
                          {t("lotDoc.release", "Pusti kupcu")}
                        </button>
                      )}
                      {smeDaPusta && d.state === "RELEASED" && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={busy === d.id}
                          onClick={() => void run(d.id, () => lotDocumentApi.withdraw(d.id))}
                        >
                          {t("lotDoc.withdraw", "Povuci")}
                        </button>
                      )}
                      {canDelete && d.state !== "RELEASED" && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={busy === d.id}
                          onClick={() => void run(d.id, () => lotDocumentApi.remove(d.id))}
                        >
                          {t("common.delete", "Obriši")}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
